import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  listConversations,
  searchConversations,
  SearchConversationsParams,
} from '@/api/conversations';
import { queryKeys } from '@/lib/queryKeys';

type ConversationSearchFilters = Omit<SearchConversationsParams, 'offset'>;
type ConversationSearchKeyFilters = Omit<SearchConversationsParams, 'limit' | 'offset'>;

export function useConversationsQuery(enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.conversations.list(),
    queryFn: listConversations,
    enabled,
  });
}

export function useSearchConversationsQuery(params: SearchConversationsParams, enabled: boolean = true) {
  const { limit = 20, offset = 0, ...filters } = params;
  const normalizedFilters: ConversationSearchFilters = {
    search: filters.search,
    from_date: filters.from_date,
    to_date: filters.to_date,
    favorites_only: filters.favorites_only,
    limit,
  };
  const keyFilters: ConversationSearchKeyFilters = {
    search: filters.search,
    from_date: filters.from_date,
    to_date: filters.to_date,
    favorites_only: filters.favorites_only,
  };

  return useQuery({
    queryKey: queryKeys.conversations.search(keyFilters),
    queryFn: () => searchConversations({ ...normalizedFilters, limit, offset }),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useInfiniteSearchConversationsQuery(
  filters: Omit<SearchConversationsParams, 'limit' | 'offset'>,
  limit: number = 20,
  enabled: boolean = true
) {
  const normalizedFilters: ConversationSearchFilters = {
    search: filters.search,
    from_date: filters.from_date,
    to_date: filters.to_date,
    favorites_only: filters.favorites_only,
    limit,
  };
  const keyFilters: ConversationSearchKeyFilters = {
    search: filters.search,
    from_date: filters.from_date,
    to_date: filters.to_date,
    favorites_only: filters.favorites_only,
  };

  return useInfiniteQuery({
    queryKey: queryKeys.conversations.search(keyFilters),
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      searchConversations({
        ...normalizedFilters,
        offset: pageParam,
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) {
        return undefined;
      }

      return allPages.length * limit;
    },
    enabled,
  });
}
